import React, { useEffect, useRef, useState } from "react";

function Counter({ to, suffix = "", duration = 1600 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();

            const tick = (now) => {
              const p = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);

              setVal(Math.round(to * eased));

              if (p < 1) requestAnimationFrame(tick);
            };

            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );

    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [to, duration]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

const STATS = [
  {
    value: 100,
    suffix: "+",
    label: "Locations Planned",
    sub: "Pan-India network rollout",
  },
  {
    value: 24,
    suffix: "M+",
    label: "Litres of Biofuel",
    sub: "Annual production target",
  },
  {
    value: 25,
    suffix: "k",
    label: "EV Charge Sessions",
    sub: "Expected Monthly capacity by 2029",
  },
  {
    value: 50,
    suffix: "+",
    label: "CBG Plants",
    sub: "Compressed Biogas facilities",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      <div
        className="eco-blob bg-eco-light"
        style={{
          width: 300,
          height: 300,
          top: "10%",
          right: "-20%",
          opacity: 0.15,
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 md:gap-14 items-start">
          
          {/* Left content */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 reveal">
            <span className="overline" data-testid="about-overline">
              Our Mission
            </span>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mt-4 leading-tight tracking-tight break-words">
              Engineering India&apos;s
              <br />
              <span className="gradient-text-eco">clean energy</span> backbone.
            </h2>

            <p className="mt-6 text-white/65 text-sm sm:text-base md:text-lg leading-relaxed break-words">
              INDIANECOFUELS will build the green energy ecosystem India needs.
              We will deliver biofuels, Bio-CNG plants, nationwide EV charging
              corridors, and reduce automobile pollution. By blending deep
              technology with sustainable engineering, we will shape the future
              of energy in India.
            </p>

            <div className="mt-8 flex items-center gap-4">
              <div className="h-px flex-1 bg-gradient-to-r from-eco-light/60 to-transparent" />
              <span className="font-mono-eco text-[10px] sm:text-[11px] tracking-widest text-eco-light/80 uppercase whitespace-nowrap">
                Est. 2026 · India
              </span>
            </div>
          </div>

          {/* Right stats */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {STATS.map((s, i) => (
              <div
                key={i}
                data-testid={`about-stat-${i}`}
                className="reveal glass-card p-5 sm:p-7 relative grain card-glow-green"
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty(
                    "--mx",
                    `${e.clientX - r.left}px`
                  );
                  e.currentTarget.style.setProperty(
                    "--my",
                    `${e.clientY - r.top}px`
                  );
                }}
                style={{
                  minHeight: window.innerWidth < 640 ? 170 : 210,
                }}
              >
                <div className="font-mono-eco text-[10px] tracking-widest text-eco-light/70 uppercase mb-4">
                  0{i + 1} / 04
                </div>

                <div className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight break-words">
                  <Counter to={s.value} suffix={s.suffix} />
                </div>

                <div className="mt-3 text-white font-medium text-sm sm:text-base">
                  {s.label}
                </div>

                <div className="mt-1 text-xs sm:text-sm text-white/50 leading-relaxed">
                  {s.sub}
                </div>

                <div className="absolute top-5 right-5 w-2 h-2 rounded-full bg-saffron" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}